import React from 'react';

import DateHelper from '../../../../helpers/dateHelper';
import TimesheetHelper from '../../../../helpers/timesheetHelper';
import { Day, Period } from '../../../../typescript/dateTypes';
import { EntryLocationInfo } from '../../../../typescript/infoTypes';
import { StoreMonthYear, StoreSettings, StoreTimesheetCode, StoreTimesheetEntry, StoreTimesheetFromClient, StoreUser } from '../../../../typescript/storeTypes';
import { MultiEdit, NavigationEvent } from '../../../../typescript/timesheetTypes';
import Timesheet from './Timesheet';
import TimesheetRow from './TimesheetRow';

const CollapsedTimesheetCodes: React.FC<Props> = ({
  collapsedTimesheetCodes,
  orderedTimesheetCodes,
  expanded,
  onToggleExpanded,
  user,
  days,
  period,
  settings,
  timesheetsFromClient,
  onEntryChange,
  onTimesheetCodeExpand,
  canIncomplete,
  canComplete,
  incompleteTimesheetCode,
  completeTimesheetCode,
  deleteTimesheetCode,
  getTimesheetsFromClientInfo,
  incompleteMonths,
  onNavigateEntry,
  activeEntry,
  setActiveEntryRequested,
  multiSelect,
  onMultiEditSubmit,
  onMultiEditCancel,
  resetToDefaultEntry,
  entriesReadOnly,
  canDeleteTimesheetCode,
  isAccessibleUser,
  canUploadClientTimesheet
}) => {

  const getTotalHoursForDay = (day: Day): number => {
    let total = 0;
    collapsedTimesheetCodes.timesheetCodes.forEach(tsc => {
      const entry = TimesheetHelper.getEntry(tsc, day);
      total += entry.normalHours + entry.extraHours + entry.standByHours + entry.interventionHours;
    });
    return total;
  };

  const hasSpecialHours = (day: Day): boolean => {
    return collapsedTimesheetCodes.timesheetCodes.some(tsc => {
      const entry = TimesheetHelper.getEntry(tsc, day);
      return entry.extraHours > 0 || entry.standByHours > 0 || entry.interventionHours > 0;
    });
  };

  if (!collapsedTimesheetCodes || collapsedTimesheetCodes.count === 0) return null;

  return (
    <div className="collapsed-timesheetcodes-container">
      <div className="collapsed-timesheetcodes-summary" onClick={onToggleExpanded}>
        <div className="collapsed-label">
          <i className={`fa fa-${expanded ? 'angle-up' : 'angle-down'}`} />
          <span className="collapsed-timesheetcodes-text">{`${collapsedTimesheetCodes.count} more timesheet codes`}</span>          
        </div>
        <div className={`collapsed-code-days${expanded ? ' collapsed-days-hidden' : ''}`}>
          {settings.monthViewSelected ? '' : <div className="timesheet-row-filler-left"/>}
          <div className={`timesheet-code-days${settings.monthViewSelected ? ' month-timesheet-code-days-width' : ' timesheet-days-weekly week-timesheet-code-days-width'}`}>
            {days.map((day) => {
              const isWeekend = DateHelper.isWeekend(day);
              if (isWeekend && settings.hideWeekends) {
                return (settings.monthViewSelected ?
                  <div key={day.dateString} className="weekend-collapsed"/> : null);
              }
              
              const totalHours = getTotalHoursForDay(day);
              const specialHours = hasSpecialHours(day);
              return (
                <div key={day.dateString}
                  className={`day-hours-container ${isWeekend ? 'weekend' : ''}`}>
                  <div className={`${settings.monthViewSelected ? 'day-hours' : 'day-hours-weekly'}${isWeekend ? ' weekend' : ''}`}>
                    {totalHours > 0 && <div className={`collapsed-total-day-hours${specialHours ? ' extra' : ''}`}>{totalHours.toFixed(2)}</div>}
                </div>
                </div>
              );
            })}
          </div>
          {settings.monthViewSelected ? '' : <div className="timesheet-row-filler-right"/>}
        </div>
        <span className="collapsed-total-hours">{collapsedTimesheetCodes.totalHours.toFixed(2)}</span>
      </div>
      {expanded && (
        <div className="collapsed-timesheetcodes-list">
          {collapsedTimesheetCodes.timesheetCodes.map((tsc, idx) => (
            <TimesheetRow
              key={tsc.id || idx}
              timesheetCode={tsc}
              user={user}
              days={days}
              period={period}
              settings={settings}
              timesheetsFromClient={timesheetsFromClient[tsc.assignmentIds?.[0]] !== undefined ? timesheetsFromClient[tsc.assignmentIds?.[0]] : []}
              onEntryChange={(entry, day, timesheetCode) => onEntryChange(entry, day, timesheetCode)}
              onTimesheetCodeExpand={(timesheetCode, fromEntry) => onTimesheetCodeExpand(timesheetCode, fromEntry)}
              canIncomplete={canIncomplete}
              canComplete={canComplete}
              incompleteTimesheetCode={tsCodeId => incompleteTimesheetCode(tsCodeId)}
              completeTimesheetCode={tsCodeId => completeTimesheetCode(tsCodeId)}
              deleteTimesheetCode={tsCodeId => deleteTimesheetCode(tsCodeId)}
              getTimesheetsFromClientInfo={assignmentId => getTimesheetsFromClientInfo(assignmentId)}
              incompleteMonths={incompleteMonths}
              tsCodeIndex={orderedTimesheetCodes.findIndex(tc => tc.id === tsc.id)}
              onNavigateEntry={(event, sourceEntry) => onNavigateEntry(event, sourceEntry)}
              activeEntry={activeEntry.tsCodeId === tsc.id ? activeEntry : Timesheet.defaultActiveEntry}
              setActiveEntryRequested={setActiveEntryRequested}
              multiSelect={multiSelect.tsCodeIndex === orderedTimesheetCodes.findIndex(tc => tc.id === tsc.id) ? multiSelect : Timesheet.defaultMultiSelect}
              onMultiEditSubmit={multiEdit => onMultiEditSubmit(multiEdit)}
              onMultiEditCancel={() => onMultiEditCancel()}
              resetToDefaultEntry={() => resetToDefaultEntry()}
              entriesReadOnly={tsc.isReadOnly || entriesReadOnly}
              canDeleteTimesheetCode={canDeleteTimesheetCode}
              isAccessibleUser={isAccessibleUser}
              canUploadClientTimesheet={canUploadClientTimesheet}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type Props = {
  collapsedTimesheetCodes: {
    totalHours: number;
    count: number;
    timesheetCodes: StoreTimesheetCode[];
  };
  orderedTimesheetCodes: StoreTimesheetCode[];
  expanded: boolean;
  onToggleExpanded: () => void;
  user: StoreUser;
  days: Day[];
  period: Period;
  settings: StoreSettings;
  timesheetsFromClient: { [key: number]: StoreTimesheetFromClient[] };
  onEntryChange: (entry: StoreTimesheetEntry, day: Day, timesheetCode: StoreTimesheetCode) => void;
  onTimesheetCodeExpand: (timesheetCode: StoreTimesheetCode, fromEntry?: boolean) => void;
  canIncomplete: boolean;
  canComplete: boolean;
  incompleteTimesheetCode: (tsCodeId: number) => void;
  completeTimesheetCode: (tsCodeId: number) => void;
  deleteTimesheetCode: (tsCodeId: number) => void;
  getTimesheetsFromClientInfo: (assignmentId: number) => void;
  incompleteMonths: StoreMonthYear[];
  onNavigateEntry: (event: NavigationEvent, sourceEntry: EntryLocationInfo) => void;
  activeEntry: EntryLocationInfo;
  setActiveEntryRequested: boolean;
  multiSelect: any;
  onMultiEditSubmit: (multiEdit: MultiEdit) => void;
  onMultiEditCancel: () => void;
  resetToDefaultEntry: () => void;
  entriesReadOnly: boolean;
  canDeleteTimesheetCode: boolean;
  isAccessibleUser: boolean;
  canUploadClientTimesheet: boolean;
}

export default CollapsedTimesheetCodes;
